import { AlertTriangle, FileClock } from 'lucide-react';
import { Link } from 'react-router-dom';

import { useMcfOutages, type McfOutage } from '../../api/queries/mcf';
import { useProvisionalDocuments } from '../../api/queries/reports';
import { Permissions } from '../../billing-host/permission-codes';
import { usePermissions } from '../../billing-host/permissions';
import { t } from '../../lib/billing-i18n';
import { cn } from '../../lib/utils';
import { formatProvisionalInstant } from '../invoices/ProvisionalIssue';

/**
 * The outage notices of every screen (arrêté n°2025-0049):
 * - for a user who declares outages, each open outage of an MCF with whether
 *   its art. 7 c) notification is recorded, and the way to record it;
 * - for a user who reads `reports:sales`, the provisional documents past their
 *   48 hours (art. 6 g)): the server returns only the overdue ones, the first
 *   200 by deadline are counted.
 * A cashier without those permissions sees nothing here: the till shows the
 * provisional path itself.
 */

/** Documents read to count the overdue ones (the route's page limit). */
const OVERDUE_SCAN_LIMIT = 200;

/** Where the outage is declared and its notification recorded (the settings screen). */
const OUTAGE_SETTINGS_PATH = '/settings/pannes-mcf';

/** Where the provisional documents are listed with their status. */
const REGULARISATION_REPORT_PATH = '/reports/documents-a-regulariser';

function OutageNotice({ outage }: { outage: McfOutage }) {
  const notified = outage.notifiee;
  return (
    <div
      role="alert"
      data-testid="mcf-outage-notice"
      data-notified={notified}
      className={cn(
        'mb-4 flex flex-wrap items-start justify-between gap-2 rounded-xl border px-4 py-3',
        notified ? 'border-amber-300 bg-amber-50' : 'border-red-300 bg-red-50'
      )}
    >
      <div className={cn('flex items-start gap-2 text-sm', notified ? 'text-amber-900' : 'text-red-900')}>
        <AlertTriangle
          className={cn('mt-0.5 h-4 w-4 flex-shrink-0', notified ? 'text-amber-600' : 'text-red-600')}
          aria-hidden="true"
        />
        <span>
          <span className="font-semibold">
            {t('outageBanner.open')
              .replace('{nim}', outage.deviceNim)
              .replace('{date}', formatProvisionalInstant(outage.declaredAt))}
          </span>{' '}
          — {notified ? t('outageBanner.notified') : t('outageBanner.notNotified')}
        </span>
      </div>
      <Link
        to={OUTAGE_SETTINGS_PATH}
        className={cn(
          'rounded-lg px-2 py-1 text-sm font-medium underline',
          notified ? 'text-amber-900 hover:bg-amber-100' : 'text-red-900 hover:bg-red-100'
        )}
      >
        {notified ? t('outageBanner.manage') : t('outageBanner.record')}
      </Link>
    </div>
  );
}

function OverdueNotice({ count, more }: { count: number; more: boolean }) {
  const text = more
    ? t('outageBanner.overdue.more')
    : count === 1
      ? t('outageBanner.overdue.one')
      : t('outageBanner.overdue.many');
  return (
    <div
      role="alert"
      data-testid="provisional-overdue-notice"
      className="mb-4 flex flex-wrap items-start justify-between gap-2 rounded-xl border border-red-300 bg-red-50 px-4 py-3"
    >
      <div className="flex items-start gap-2 text-sm text-red-900">
        <FileClock className="mt-0.5 h-4 w-4 flex-shrink-0 text-red-600" aria-hidden="true" />
        <span>{text.replace('{n}', String(count))}</span>
      </div>
      <Link
        to={REGULARISATION_REPORT_PATH}
        className="rounded-lg px-2 py-1 text-sm font-medium text-red-900 underline hover:bg-red-100"
      >
        {t('outageBanner.overdue.link')}
      </Link>
    </div>
  );
}

export function McfOutageBanner() {
  const { hasPermission } = usePermissions();
  const canDeclare = hasPermission(Permissions.MCF_DECLARER_PANNE);
  const canReadReport = hasPermission('reports:sales');
  const outages = useMcfOutages({ enabled: canDeclare });
  const documents = useProvisionalDocuments(null, {
    limit: OVERDUE_SCAN_LIMIT,
    status: 'EN_RETARD',
    enabled: canReadReport,
  });

  const open = canDeclare ? (outages.data ?? []).filter((outage) => outage.endedAt === null) : [];
  const first = canReadReport ? documents.data?.pages[0] : undefined;
  const overdue = first?.documents.length ?? 0;
  if (open.length === 0 && overdue === 0) return null;

  return (
    <div data-testid="mcf-outage-banner">
      {open.map((outage) => (
        <OutageNotice key={outage.id} outage={outage} />
      ))}
      {overdue > 0 ? <OverdueNotice count={overdue} more={first?.nextCursor != null} /> : null}
    </div>
  );
}

export default McfOutageBanner;
